/**
 * ContactSection — email + phone for the Personal Details tab.
 *
 * Ports Flutter `personal_details_widget.dart` contactDetailsWidget:
 *   - Rendered as its own section, FIRST on the page. The card header is
 *     "Contact Details" for an adult and "Guardian's Contact Details" for a
 *     minor (the contact belongs to the guardian) — set by the parent via the
 *     SectionCard header, driven by `isAdult`.
 *   - Permission-gated on `Patient_Contacts` (view) in edit mode — handled by
 *     the parent (`isCreateMode || usePermission(['Patient_Contacts'],{view})`).
 *   - EDIT MODE uses a read-only + edit-toggle UX: each field is locked with a
 *     pencil (edit) + copy affordance; clicking the pencil enables it and swaps
 *     to a tick (confirm) + clear (revert to the loaded value). CREATE MODE
 *     keeps the fields directly editable.
 *   - Validation: server-side carrier check (shared `useValidatePhoneNumber`) plus a
 *     backend duplicate check (`/api/Patients/ValidateEmail` /
 *     `/ValidatePhoneNumber`). Per Flutter, the duplicate check runs ONLY in
 *     edit mode, for ADULTS, and only when the value changed from the loaded
 *     record (a minor's guardian contact is shared, so duplicates are allowed).
 */

import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { requiredTone } from './required-tone';
import { Pencil, Check, X, Copy } from 'lucide-react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { CountryDialPicker, splitPhone } from '@scripsteam/scrips-signal-ds';
import { useValidatePhoneNumber } from '@/patients/services/use-validate-phone-number';
import {
  useCheckDuplicateEmail,
  useCheckDuplicatePhone,
} from '@/patients/services/use-check-duplicate-contact';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { isFlutterEmail } from '@/auth/email-validation';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import type { PersonalDetailsFormValues } from './personal-details-schema';
import { useMissingFields } from './missing-fields-context';

const ICON_BTN =
  'icon-action focus:outline-none focus:ring-1 focus:ring-brand-primary';

export interface ContactSectionProps {
  /** Edit mode (existing patient). Drives the read-only + edit-toggle UX and
   *  the duplicate checks (Flutter only validates contact in edit mode). */
  isEdit: boolean;
  patientId: string | null;
  /** Loaded values — gate the duplicate check on "changed from initial" and
   *  power the clear/revert affordance. */
  initialEmail: string;
  initialPhone: string;
}

export function ContactSection({
  isEdit,
  patientId,
  initialEmail,
  initialPhone,
}: ContactSectionProps) {
  // PROD-2743 · switch ON: DS `edit` / `check` / `close` / `copy` glyphs (16px) on the icon buttons;
  // they stay brand because they are interactive. OFF = today's lucide.
  const { register, control, setValue, formState } =
    useFormContext<PersonalDetailsFormValues>();
  const { errors } = formState;
  const missing = useMissingFields();

  const email = useWatch({ control, name: 'email' });
  const phoneStored = useWatch({ control, name: 'phoneNumber' });
  const isAdult = useWatch({ control, name: 'isAdult' });

  // Edit-toggle: edit mode starts locked; create mode is directly editable.
  const [editingEmail, setEditingEmail] = useState(!isEdit);
  const [editingPhone, setEditingPhone] = useState(!isEdit);

  // ── Phone carrier validation (same gate as DemographicsSection had) ──
  const { local: storedLocal } = splitPhone(phoneStored ?? '');
  const { dial: storedDial } = splitPhone(phoneStored ?? '');
  const e164 = `${storedDial}${storedLocal}`;
  const debouncedLookup = useDebounce(e164, 500);
  const validatePhone = useValidatePhoneNumber();
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (storedLocal.length === 0) {
      setPhoneError(null);
      return;
    }
    if (storedLocal.length < 7) {
      setPhoneError('Please, provide a valid number');
      return;
    }
    setPhoneError(null);
    validatePhone.mutate(
      { contactNumber: debouncedLookup },
      {
        onSuccess: (result) =>
          setPhoneError(result.valid ? null : 'Please, provide a valid number'),
        onError: () => setPhoneError('Could not validate phone number'),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedLookup]);

  // ── Backend duplicate checks — edit-mode + adult + changed-from-initial ──
  const debouncedEmail = useDebounce(email ?? '', 500);
  const emailDupEnabled =
    isAdult === true &&
    isEdit &&
    isFlutterEmail(debouncedEmail) &&
    debouncedEmail !== initialEmail;
  const emailDupQ = useCheckDuplicateEmail(
    emailDupEnabled ? { emailAddress: debouncedEmail, patientId } : null,
  );
  const emailDuplicate = emailDupQ.data === true;

  const debouncedPhone = useDebounce(phoneStored ?? '', 500);
  const phoneDupEnabled =
    isAdult === true &&
    isEdit &&
    !phoneError &&
    storedLocal.length >= 7 &&
    debouncedPhone !== initialPhone;
  const phoneDupQ = useCheckDuplicatePhone(
    phoneDupEnabled ? { contactNumber: debouncedPhone, patientId } : null,
  );
  const phoneDuplicate = phoneDupQ.data === true;

  const emailLocked = isEdit && !editingEmail;
  const phoneLocked = isEdit && !editingPhone;

  return (
    <div className="grid gap-6">
      {/* Email — Flutter renders EMAIL ADDRESS first, CONTACT NUMBER second. */}
      <div>
        <FieldLabel required requiredTone={requiredTone(email)}>Email address</FieldLabel>
        <div className="mt-1 flex items-center gap-1">
          <TextField
            type="email"
            {...register('email', { setValueAs: (v) => (v === '' ? null : v) })}
            placeholder="Enter email address"
            className="flex-1" size="compact"
            disabled={emailLocked}
            aria-invalid={!!errors.email || emailDuplicate}
            status={missing.has('email') ? 'error' : undefined}
          />
          {isEdit ? (
            editingEmail ? (
              <>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Confirm email"
                  onClick={() => setEditingEmail(false)}
                >
                  <Check className="size-4" />
                </button>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Cancel email edit"
                  onClick={() => {
                    setValue('email', initialEmail || null, { shouldDirty: true });
                    setEditingEmail(false);
                  }}
                >
                  <X className="size-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Edit email"
                  onClick={() => setEditingEmail(true)}
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Copy email"
                  onClick={() => void navigator.clipboard?.writeText(email ?? '')}
                >
                  <Copy className="size-4" />
                </button>
              </>
            )
          ) : null}
        </div>
        {emailDupQ.isFetching ? (
          <p className="mt-1 text-xs text-text-muted">Validating email…</p>
        ) : emailDuplicate ? (
          <p className="mt-1 text-xs text-status-error">
            This email is already used
          </p>
        ) : errors.email ? (
          <p className="mt-1 text-xs text-status-error">{errors.email.message}</p>
        ) : null}
      </div>

      {/* Contact number (phone) */}
      <div data-missing-field={missing.has('phoneNumber') || undefined}>
        <FieldLabel required requiredTone={requiredTone(storedLocal)}>Contact number</FieldLabel>
        <div className="mt-1 flex items-center gap-1">
          <div className="flex-1">
            <CountryDialPicker
              value={phoneStored ?? ''}
              onChange={(next) =>
                setValue('phoneNumber', next, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              invalid={!!phoneError || phoneDuplicate || missing.has('phoneNumber')}
              disabled={phoneLocked}
            />
          </div>
          {isEdit ? (
            editingPhone ? (
              <>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Confirm phone"
                  onClick={() => setEditingPhone(false)}
                >
                  <Check className="size-4" />
                </button>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Cancel phone edit"
                  onClick={() => {
                    setValue('phoneNumber', initialPhone, { shouldDirty: true });
                    setEditingPhone(false);
                  }}
                >
                  <X className="size-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Edit phone"
                  onClick={() => setEditingPhone(true)}
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  className={ICON_BTN}
                  aria-label="Copy phone"
                  onClick={() => void navigator.clipboard?.writeText(phoneStored ?? '')}
                >
                  <Copy className="size-4" />
                </button>
              </>
            )
          ) : null}
        </div>
        {validatePhone.isPending || phoneDupQ.isFetching ? (
          <p className="text-xs text-text-muted mt-1">Validating phone…</p>
        ) : phoneError ? (
          <p className="text-xs text-status-error mt-1">{phoneError}</p>
        ) : phoneDuplicate ? (
          <p className="text-xs text-status-error mt-1">
            This phone number is already used
          </p>
        ) : errors.phoneNumber ? (
          <p className="text-xs text-status-error mt-1">
            {errors.phoneNumber.message}
          </p>
        ) : null}
      </div>
    </div>
  );
}
